const CJK_RE = /[\u4e00-\u9fff]/;
const VIETNAMESE_RE = /[àáảãạăằắẳẵặâầấẩẫậèéẻẽẹêềếểễệìíỉĩịòóỏõọôồốổỗộơờớởỡợùúủũụưừứửữựỳýỷỹỵđ]/i;
/** Letters that distinguish Vietnamese from English/French (e.g. Sautéed). */
const STRONG_VIETNAMESE_RE = /[ăằắẳẵặâầấẩẫậđêềếểễệôồốổỗộơờớởỡợưừứửữự]/i;

export function hasCjk(value) {
	return CJK_RE.test(`${value || ''}`);
}

export function hasVietnamese(value) {
	return VIETNAMESE_RE.test(`${value || ''}`);
}

export function hasStrongVietnamese(value) {
	return STRONG_VIETNAMESE_RE.test(`${value || ''}`);
}

function splitMixedLatinAndVietnamese(text) {
	const commaParts = `${text || ''}`.split(/\s*,\s*/).map((part) => part.trim()).filter(Boolean);
	if (commaParts.length < 2) return null;
	const en = [];
	const cn = [];
	const vn = [];
	commaParts.forEach((part) => {
		if (hasCjk(part)) cn.push(part);
		else if (hasStrongVietnamese(part)) vn.push(part);
		else en.push(part);
	});
	if (!en.length || !vn.length) return null;
	return {
		en: en.join(', '),
		cn: cn.join(', '),
		vn: vn.join(', '),
	};
}

/** Driver part of link_event_name, before trailing pick-up time (e.g. "Zahra, Mohamed 9:30:00 AM"). */
export function driverNameFromLinkEventName(linkName) {
	if (!linkName || typeof linkName !== 'string') return '';
	return linkName
		.trim()
		.replace(/\s+\d{1,2}:\d{2}(?::\d{2})?(\s*[AP]M)?$/i, '')
		.trim();
}

export function parseDriverLastFirstParts(linkEventName) {
	const name = driverNameFromLinkEventName(linkEventName);
	if (!name) return { first: '', last: '' };
	if (name.includes(',')) {
		const [last, ...rest] = name.split(',');
		return { last: last.trim(), first: rest.join(',').trim() };
	}
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 1) return { first: parts[0], last: '' };
	return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
}

/** Last Name, First Name. Strips pick-up time from transportation link titles. */
export function formatDriverLastFirst(linkEventName) {
	const { first, last } = parseDriverLastFirstParts(linkEventName);
	if (last && first) return `${last}, ${first}`;
	return last || first || '';
}

function titleCaseName(value) {
	return `${value || ''}`
		.trim()
		.split(/\s+/)
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ');
}

function normalizeNameKey(value) {
	return `${value || ''}`.trim().toLowerCase().replace(/[^a-z0-9\u4e00-\u9fff]+/gi, '');
}

function lastNameInitial(last) {
	const trimmed = `${last || ''}`.trim();
	if (!trimmed) return '';
	const ch = trimmed.charAt(0);
	return CJK_RE.test(ch) ? ch : ch.toUpperCase();
}

function personLastFirst(person) {
	const first = `${person?.firstname || person?.firstName || ''}`.trim();
	const last = `${person?.lastname || person?.lastName || ''}`.trim();
	if (first || last) return { first, last };
	const name = `${person?.name || ''}`.trim();
	if (!name) return { first: '', last: '' };
	return parseDriverLastFirstParts(name);
}

function personPreferredName(person) {
	return `${person?.preferred_name || person?.preferredName || person?.name_cn || ''}`.trim();
}

export function formatFirstLastInitialPreferred(first, last, preferred) {
	const firstName = titleCaseName(first);
	const initial = lastNameInitial(last);
	const pref = `${preferred || ''}`.trim();
	const latin = [firstName, initial].filter(Boolean).join(' ');
	if (latin && pref) return `${latin} ${pref}`;
	if (latin) return latin;
	return pref;
}

/** First Name + last initial + Preferred Name (e.g. Dongming W 王东明). */
export function formatInfoScreenDriverName(linkEventName, people = []) {
	const parsed = parseDriverLastFirstParts(linkEventName);
	if (!parsed.first && !parsed.last) return '';
	const wantKey = `${normalizeNameKey(parsed.last)}|${normalizeNameKey(parsed.first)}`;
	const wantCombined = normalizeNameKey(`${parsed.last}${parsed.first}`);
	const match = (Array.isArray(people) ? people : []).find((person) => {
		const parts = personLastFirst(person);
		const key = `${normalizeNameKey(parts.last)}|${normalizeNameKey(parts.first)}`;
		if (key === wantKey) return true;
		const combined = normalizeNameKey(`${parts.last}${parts.first}`);
		const swapped = normalizeNameKey(`${parts.first}${parts.last}`);
		const fromName = normalizeNameKey(person?.name);
		return Boolean(wantCombined) && (combined === wantCombined || swapped === wantCombined || fromName === wantCombined);
	});
	const first = match ? (personLastFirst(match).first || parsed.first) : parsed.first;
	const last = match ? (personLastFirst(match).last || parsed.last) : parsed.last;
	const preferred = match ? personPreferredName(match) : '';
	return formatFirstLastInitialPreferred(first, last, preferred);
}

/** Original trailing pick-up time from link_event_name (e.g. "9:30:00 AM"). */
export function pickupTimeSuffixFromLinkEventName(linkName) {
	if (!linkName || typeof linkName !== 'string') return '';
	const match = linkName.trim().match(/\s+(\d{1,2}:\d{2}(?::\d{2})?(?:\s*[AP]M)?)$/i);
	return match ? match[1].replace(/\s+/g, ' ').trim() : '';
}

/** Info Screen 1 driver cell: formatted name, then original start time. */
export function formatInfoScreenDriverNameWithPickupTime(linkEventName, people = []) {
	const name = formatInfoScreenDriverName(linkEventName, people);
	const time = pickupTimeSuffixFromLinkEventName(linkEventName);
	if (name && time) return `${name} ${time}`;
	return name || time;
}

/** HH:mm pick-up time from one-day list driver cell (`link_event_name`). */
export function parseDriverPickupTime24(linkName) {
	if (!linkName || typeof linkName !== 'string') return '';
	const text = linkName.trim();
	const ampm = text.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)\s*$/i);
	if (ampm) {
		let hour = parseInt(ampm[1], 10);
		const minute = ampm[2];
		const meridiem = ampm[3].toUpperCase();
		if (Number.isNaN(hour)) return '';
		if (meridiem === 'PM' && hour < 12) hour += 12;
		if (meridiem === 'AM' && hour === 12) hour = 0;
		return `${String(hour).padStart(2, '0')}:${minute}`;
	}
	const hour24 = text.match(/\s+(\d{1,2}):(\d{2})(?::\d{2})?\s*$/);
	if (hour24) {
		const hour = parseInt(hour24[1], 10);
		if (Number.isNaN(hour)) return '';
		return `${String(hour).padStart(2, '0')}:${hour24[2]}`;
	}
	return '';
}

/** Preferred name if present; otherwise Last Name, First Name. */
export function formatInfoScreenAppointmentCustomerName(customer, eventData) {
	const preferred = `${
		customer?.name_cn
		|| customer?.preferred_name
		|| eventData?.client_preferred_name
		|| eventData?.preferred_name
		|| ''
	}`.trim();
	if (preferred) return preferred;

	const last = `${customer?.lastname ?? customer?.lastName ?? ''}`.trim();
	const first = `${customer?.firstname ?? customer?.firstName ?? ''}`.trim();
	if (last && first) return `${last}, ${first}`;
	if (last) return last;
	if (first) return first;
	return `${customer?.name || eventData?.client_name || ''}`.trim();
}

export function splitBilingualText(value) {
	const { en, cn } = splitTrilingualText(value);
	return { en, cn };
}

/** Dedicated location fields, with slash-split fallback for older mixed-language strings. */
export function activityLocationParts(event, { includeVn = false } = {}) {
	const location = `${event?.event_location || ''}`.trim();
	const locationCn = `${event?.event_location_cn || ''}`.trim();
	const locationVn = `${event?.event_location_vn || ''}`.trim();
	if (locationCn || locationVn) {
		return { en: location, cn: locationCn, vn: includeVn ? locationVn : '' };
	}
	const split = includeVn ? splitTrilingualText(location) : splitBilingualText(location);
	return { en: split.en, cn: split.cn, vn: includeVn ? split.vn : '' };
}

export function splitTrilingualText(value) {
	const text = `${value || ''}`.trim();
	if (!text) return { en: '', cn: '', vn: '' };
	const parts = text.split(/\s*\/\s*/).map((part) => part.trim()).filter(Boolean);
	if (parts.length >= 2) {
		const cn = parts.filter((part) => hasCjk(part));
		const vn = parts.filter((part) => !hasCjk(part) && hasStrongVietnamese(part));
		const en = parts.filter((part) => !hasCjk(part) && !hasStrongVietnamese(part));
		if (cn.length || vn.length) {
			return { en: en.join(' / '), cn: cn.join(' / '), vn: vn.join(' / ') };
		}
		if (parts.length === 3) {
			return { en: parts[0], cn: parts[1], vn: parts[2] };
		}
	}
	const mixed = splitMixedLatinAndVietnamese(text);
	if (mixed) return mixed;
	if (hasCjk(text) && !/[A-Za-z]/.test(text)) return { en: '', cn: text, vn: '' };
	if (hasStrongVietnamese(text) && !hasCjk(text) && !/[A-Za-z]{3,}/.test(text)) {
		return { en: '', cn: '', vn: text };
	}
	return { en: text, cn: '', vn: '' };
}

function collectMealLangBlocks(items) {
	const en = [];
	const cn = [];
	for (const item of items) {
		const title = `${item?.title || ''}`.trim();
		const titleCn = `${item?.title_cn || ''}`.trim();
		const ingredients = `${item?.ingredients || ''}`.trim();
		if (title) {
			const split = splitBilingualText(title);
			if (split.en) en.push(split.en);
			if (split.cn) cn.push(split.cn);
		}
		if (titleCn) cn.push(titleCn);
		if (ingredients) {
			if (hasCjk(ingredients) && !/[A-Za-z]{2,}/.test(ingredients)) cn.push(ingredients);
			else en.push(ingredients);
		}
	}
	return { en: en.join('\n'), cn: cn.join('\n') };
}

function collectMealLangBlocks3(items) {
	const en = [];
	const cn = [];
	const vn = [];
	for (const item of items) {
		const title = `${item?.title || ''}`.trim();
		const titleCn = `${item?.title_cn || ''}`.trim();
		const titleVn = `${item?.title_vn || ''}`.trim();
		const ingredients = `${item?.ingredients || ''}`.trim();
		if (title) {
			const split = splitTrilingualText(title);
			if (split.en) en.push(split.en);
			if (split.cn) cn.push(split.cn);
			if (split.vn) vn.push(split.vn);
		}
		if (titleCn) cn.push(titleCn);
		if (titleVn) vn.push(titleVn);
		if (ingredients) {
			const split = splitTrilingualText(ingredients);
			if (split.en) en.push(split.en);
			if (split.cn) cn.push(split.cn);
			if (split.vn) vn.push(split.vn);
		}
	}
	return { en: en.join('\n'), cn: cn.join('\n'), vn: vn.join('\n') };
}

function parseLegacyMealDescription(description) {
	const d = description || '';
	let lunch = '';
	const firstPart = d.split(/Snack|snack/)[0];
	if (firstPart.includes('Lunch') || firstPart.includes('lunch')) {
		const lunchParts = firstPart.split(/Lunch|lunch/);
		if (lunchParts.length > 1) lunch = lunchParts[1].replace(/:/g, '').trim();
	}
	let breakfast = '';
	const secondPart = firstPart.split(/Lunch|lunch/)[0];
	if (secondPart.includes('Breakfast') || secondPart.includes('breakfast')) {
		const breakfastParts = secondPart.split(/Breakfast|breakfast/);
		if (breakfastParts.length > 1) breakfast = breakfastParts[1].replace(/:/g, '').trim();
	}
	return { breakfast, lunch };
}

/**
 * Breakfast + lunch only. English and Chinese are separate strings (not stacked with Vietnamese).
 */
export function buildScreen2MealSections(todayMealPlanEvents) {
	const buckets = { breakfast: [], lunch: [] };

	for (const item of todayMealPlanEvents || []) {
		const type = String(item.meal_type || '').trim().toLowerCase();
		if (type === 'snack') continue;

		if (type === 'breakfast' || type === 'lunch') {
			buckets[type].push(item);
			continue;
		}

		const parsed = parseLegacyMealDescription(item.description || '');
		if (parsed.breakfast || parsed.lunch) {
			if (parsed.breakfast) buckets.breakfast.push({ title: parsed.breakfast, title_cn: '' });
			if (parsed.lunch) buckets.lunch.push({ title: parsed.lunch, title_cn: '' });
			continue;
		}

		buckets.breakfast.push(item);
	}

	return {
		breakfast: collectMealLangBlocks(buckets.breakfast),
		lunch: collectMealLangBlocks(buckets.lunch),
	};
}

export function buildScreen3MealSections(todayMealPlanEvents) {
	const buckets = { breakfast: [], lunch: [] };

	for (const item of todayMealPlanEvents || []) {
		const type = String(item.meal_type || '').trim().toLowerCase();
		if (type === 'snack') continue;

		if (type === 'breakfast' || type === 'lunch') {
			buckets[type].push(item);
			continue;
		}

		const parsed = parseLegacyMealDescription(item.description || '');
		if (parsed.breakfast || parsed.lunch) {
			if (parsed.breakfast) buckets.breakfast.push({ title: parsed.breakfast, title_cn: '', title_vn: '' });
			if (parsed.lunch) buckets.lunch.push({ title: parsed.lunch, title_cn: '', title_vn: '' });
			continue;
		}

		buckets.breakfast.push(item);
	}

	return {
		breakfast: collectMealLangBlocks3(buckets.breakfast),
		lunch: collectMealLangBlocks3(buckets.lunch),
	};
}

export const SCREEN2_ACTIVITIES_PAGE_SIZE = 5;
export const SCREEN2_APPOINTMENTS_PAGE_SIZE = 12;
export const SCREEN2_ACTIVITIES_PAGE_SWITCH_MS = 10 * 1000;
export const SCREEN3_ACTIVITIES_PAGE_SIZE = 8;
export const SCREEN3_APPOINTMENTS_PAGE_SIZE = 15;

/** After this local hour, appointments, meals, and activities show the next calendar day. */
export const INFO_SCREEN_NEXT_DAY_APPOINTMENTS_HOUR = 15;

export function getInfoScreenAppointmentDay(now = new Date()) {
	const current = new Date(now);
	const day = new Date(current.getFullYear(), current.getMonth(), current.getDate());
	if (current.getHours() >= INFO_SCREEN_NEXT_DAY_APPOINTMENTS_HOUR) {
		day.setDate(day.getDate() + 1);
	}
	return day;
}
